import type { AuditSeverity } from './types';

/**
 * كتالوج أحداث التدقيق.
 * كل عملية إدارية أو حساسة يجب أن تُسجَّل بمفتاح من هنا، ولا تُستخدم نصوص حرة.
 */
export const AUDIT_ACTIONS = {
  // المنصة
  platformOwnerGranted: 'platform.owner.granted',
  platformOwnerRevoked: 'platform.owner.revoked',
  platformBootstrapped: 'platform.bootstrap.completed',
  platformSettingsUpdated: 'platform.settings.updated',
  companyModuleToggled: 'platform.module.toggled',

  // الشركات والتنظيم
  companyCreated: 'company.created',
  companyUpdated: 'company.updated',
  companyStatusChanged: 'company.status_changed',
  companySuspended: 'company.suspended',
  companyActivated: 'company.activated',
  companyDeleted: 'company.deleted',
  companyRestored: 'company.restored',
  companyPurged: 'company.purged',
  brandCreated: 'brand.created',
  brandUpdated: 'brand.updated',
  brandStatusChanged: 'brand.status_changed',
  brandArchived: 'brand.archived',
  brandDeleted: 'brand.deleted',
  brandRestored: 'brand.restored',
  brandPurged: 'brand.purged',
  branchCreated: 'branch.created',
  branchUpdated: 'branch.updated',
  branchStatusChanged: 'branch.status_changed',
  branchDeleted: 'branch.deleted',
  branchRestored: 'branch.restored',
  branchPurged: 'branch.purged',
  branchLinkedToBrand: 'branch.brand_linked',
  branchUnlinkedFromBrand: 'branch.brand_unlinked',

  // المستخدمون والعضويات
  membershipCreated: 'membership.created',
  membershipUpdated: 'membership.updated',
  membershipStatusChanged: 'membership.status_changed',
  membershipDeleted: 'membership.deleted',
  membershipRestored: 'membership.restored',
  membershipPurged: 'membership.purged',
  membershipLinkAdded: 'membership.link_added',
  membershipLinkDeleted: 'membership.link_deleted',
  membershipLinkRestored: 'membership.link_restored',
  membershipLinkPurged: 'membership.link_purged',
  membershipRolesChanged: 'membership.roles_changed',
  membershipAccessGranted: 'membership.access_granted',
  membershipAccessRevoked: 'membership.access_revoked',
  roleCreated: 'role.created',
  roleUpdated: 'role.updated',
  rolePermissionsChanged: 'role.permissions_changed',
  roleScopesChanged: 'role.scopes_changed',
  roleArchived: 'role.archived',
  roleDeleted: 'role.deleted',
  roleRestored: 'role.restored',
  supervisionCreated: 'supervision.created',
  supervisionUpdated: 'supervision.updated',
  supervisionRevoked: 'supervision.revoked',

  // المهام
  taskCreated: 'task.created',
  taskUpdated: 'task.updated',
  taskDeleted: 'task.deleted',
  completedTasksPurged: 'task.completed_purged',
  taskAssigned: 'task.assigned',
  taskUnassigned: 'task.unassigned',
  taskCompleted: 'task.completed',
  taskApproved: 'task.approved',
  taskReturned: 'task.returned',
  taskReopened: 'task.reopened',
  taskCancelled: 'task.cancelled',
  taskTemplateCreated: 'task_template.created',
  taskTemplateUpdated: 'task_template.updated',
  taskTemplateArchived: 'task_template.archived',
  taskTemplateDeleted: 'task_template.deleted',
  taskTemplateCloned: 'task_template.cloned',
  taskCloned: 'task.cloned',
  taskTemplateOccurrencesCancelled: 'task_template.occurrences_cancelled',
  taskOccurrencesGenerated: 'task_occurrences.generated',
  taskCategoryCreated: 'task_category.created',
  taskCategoryUpdated: 'task_category.updated',
  personalTaskAccessedByAdmin: 'task.personal_accessed_by_admin',

  // المرفقات
  attachmentUploaded: 'attachment.uploaded',
  attachmentDeleted: 'attachment.deleted',
  attachmentDeletionFailed: 'attachment.deletion_failed',
  retentionPolicyUpdated: 'retention_policy.updated',
  retentionSweepExecuted: 'retention.sweep_executed',

  // الإشعارات
  notificationRead: 'notification.read',

  // الوصول والإعدادات
  sensitiveExportRequested: 'report.export_requested',
  settingsUpdated: 'settings.updated',
  themeUpdated: 'theme.updated',
  unauthorizedAttempt: 'security.unauthorized_attempt',
} as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[keyof typeof AUDIT_ACTIONS];

export const CRITICAL_ACTIONS: ReadonlySet<string> = new Set<string>([
  AUDIT_ACTIONS.platformOwnerGranted,
  AUDIT_ACTIONS.platformOwnerRevoked,
  AUDIT_ACTIONS.platformBootstrapped,
  AUDIT_ACTIONS.companyStatusChanged,
  AUDIT_ACTIONS.companySuspended,
  AUDIT_ACTIONS.companyActivated,
  AUDIT_ACTIONS.companyDeleted,
  AUDIT_ACTIONS.companyRestored,
  AUDIT_ACTIONS.companyPurged,
  AUDIT_ACTIONS.brandDeleted,
  AUDIT_ACTIONS.brandRestored,
  AUDIT_ACTIONS.brandPurged,
  AUDIT_ACTIONS.branchDeleted,
  AUDIT_ACTIONS.branchRestored,
  AUDIT_ACTIONS.branchPurged,
  AUDIT_ACTIONS.membershipDeleted,
  AUDIT_ACTIONS.membershipRestored,
  AUDIT_ACTIONS.membershipPurged,
  AUDIT_ACTIONS.membershipLinkAdded,
  AUDIT_ACTIONS.membershipLinkDeleted,
  AUDIT_ACTIONS.membershipLinkRestored,
  AUDIT_ACTIONS.membershipLinkPurged,
  AUDIT_ACTIONS.membershipRolesChanged,
  AUDIT_ACTIONS.taskDeleted,
  AUDIT_ACTIONS.completedTasksPurged,
  AUDIT_ACTIONS.rolePermissionsChanged,
  AUDIT_ACTIONS.roleDeleted,
  AUDIT_ACTIONS.membershipAccessGranted,
  AUDIT_ACTIONS.membershipAccessRevoked,
  AUDIT_ACTIONS.supervisionCreated,
  AUDIT_ACTIONS.supervisionRevoked,
  AUDIT_ACTIONS.attachmentDeleted,
  AUDIT_ACTIONS.retentionSweepExecuted,
  AUDIT_ACTIONS.personalTaskAccessedByAdmin,
  AUDIT_ACTIONS.unauthorizedAttempt,
]);

export function severityForAction(action: string): AuditSeverity {
  return CRITICAL_ACTIONS.has(action) ? 'critical' : 'info';
}

/** الحقول التي يجب إزالتها قبل كتابة أي سجل تدقيق. */
export const AUDIT_REDACTED_KEYS: readonly string[] = [
  'password',
  'token',
  'access_token',
  'refresh_token',
  'secret',
  'service_role',
  'authorization',
  'apikey',
  'apiKey',
  'bootstrapToken',
];

/** إزالة القيم الحساسة من أي كائن قبل تسجيله. */
export function redactForAudit(value: unknown, depth = 0): unknown {
  if (depth > 6) return '[عمق مفرط]';
  if (value === null || value === undefined) return value ?? null;
  if (Array.isArray(value)) return value.map((v) => redactForAudit(v, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = AUDIT_REDACTED_KEYS.includes(k) ? '[محذوف]' : redactForAudit(v, depth + 1);
    }
    return out;
  }
  if (typeof value === 'string' && value.length > 2000) return `${value.slice(0, 2000)}…`;
  return value;
}
